/**
 * RolesTab — Rollenbeheer voor de Enterprise RBAC-sprint (Feb 2026).
 *
 * Onderdeel van MedewerkersSection (tab). Toont alle rollen (system + custom):
 *   - lijst met name/key/beschrijving/aantal-perms/aantal-users
 *   - nieuwe rol aanmaken
 *   - rol wijzigen (naam/description/permissions)
 *   - rol dupliceren (als startpunt)
 *   - rol uitschakelen/inschakelen
 *   - rol verwijderen (alleen custom, blocked bij actieve users)
 *
 * Rechten:
 *   - Bekijken: `roles:manage` OR owner
 *   - Wijzigen: `roles:manage` OR owner
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Plus, Edit3, Copy, Trash2, Lock, ShieldCheck, ShieldOff,
  Loader2, Users, KeyRound, X, ChevronDown, ChevronRight, Save,
  AlertTriangle, CheckCircle2,
} from "lucide-react";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Textarea } from "../../ui/textarea";
import { Badge } from "../../ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../../ui/dialog";
import { adminApi, formatApiError, useAuth } from "../../../context/AuthContext";
import PermissionMatrixEditor from "./PermissionMatrixEditor";

export default function RolesTab() {
  const { user, isOwner, hasPermission } = useAuth();
  const canManage = isOwner?.() || hasPermission("roles:manage");

  const [roles, setRoles] = useState([]);
  const [registry, setRegistry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingRole, setEditingRole] = useState(null);   // {id, ...} or null
  const [creating, setCreating] = useState(false);
  const [duplicatingFrom, setDuplicatingFrom] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rRoles, rReg] = await Promise.all([
        adminApi.get("/admin/rbac/roles"),
        adminApi.get("/admin/rbac/permission-registry"),
      ]);
      setRoles(rRoles.data.roles || []);
      setRegistry(rReg.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!canManage) {
    return (
      <div className="p-8 bg-amber-50 border border-amber-200 rounded-md text-center" data-testid="roles-no-access">
        <Lock className="w-10 h-10 text-amber-500 mx-auto mb-2" />
        <p className="text-amber-900 font-semibold">Geen toegang tot Rollenbeheer</p>
        <p className="text-xs text-amber-700 mt-1">Vereiste rechten: <code>roles:manage</code></p>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="roles-tab">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-bold text-brand-900">Rollen</h3>
        <Button size="sm" onClick={() => setCreating(true)} className="rounded-none bg-[#0055FF] text-white"
                data-testid="role-create-btn">
          <Plus className="w-3.5 h-3.5 mr-1" /> Nieuwe rol
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-8 text-slate-500 text-sm">
          <Loader2 className="w-4 h-4 inline animate-spin mr-1" /> Rollen laden…
        </div>
      ) : roles.length === 0 ? (
        <div className="p-6 text-slate-500 text-sm border border-dashed border-slate-300 rounded">
          Geen rollen gevonden.
        </div>
      ) : (
        <div className="border border-slate-200 rounded" data-testid="roles-list">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-wider text-slate-600">
              <tr>
                <th className="text-left px-3 py-2">Naam</th>
                <th className="text-left px-3 py-2">Key</th>
                <th className="text-right px-3 py-2">Rechten</th>
                <th className="text-right px-3 py-2">Medewerkers</th>
                <th className="text-left px-3 py-2">Type</th>
                <th className="text-right px-3 py-2">Acties</th>
              </tr>
            </thead>
            <tbody>
              {roles.map((r) => (
                <tr key={r.id} className={`border-b border-slate-100 hover:bg-slate-50 ${r.disabled ? "opacity-50" : ""}`}
                    data-testid={`role-row-${r.key}`}>
                  <td className="px-3 py-2">
                    <div className="font-medium text-brand-900">{r.name}</div>
                    {r.description && <div className="text-[11px] text-slate-500 truncate max-w-[280px]">{r.description}</div>}
                  </td>
                  <td className="px-3 py-2 font-mono text-[11px] text-slate-600">{r.key}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{r.permission_count}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{r.user_count}</td>
                  <td className="px-3 py-2">
                    {r.is_system ? (
                      <Badge className="bg-slate-100 text-slate-700 text-[9px] border border-slate-200">Systeem</Badge>
                    ) : (
                      <Badge className="bg-blue-100 text-blue-700 text-[9px] border border-blue-200">Custom</Badge>
                    )}
                    {r.disabled && (
                      <Badge className="ml-1 bg-amber-100 text-amber-700 text-[9px] border border-amber-200">Uit</Badge>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="inline-flex gap-1">
                      <button type="button" onClick={() => setEditingRole(r)}
                              className="p-1.5 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded"
                              title="Bewerken" data-testid={`role-edit-${r.key}`}>
                        <Edit3 className="w-3.5 h-3.5" />
                      </button>
                      <button type="button" onClick={() => setDuplicatingFrom(r)}
                              className="p-1.5 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded"
                              title="Dupliceren" data-testid={`role-duplicate-${r.key}`}>
                        <Copy className="w-3.5 h-3.5" />
                      </button>
                      {!r.is_system && r.user_count === 0 && (
                        <button type="button"
                                onClick={async () => {
                                  if (!window.confirm(`Rol '${r.name}' verwijderen?`)) return;
                                  try {
                                    await adminApi.delete(`/admin/rbac/roles/${r.id}`);
                                    toast.success("Rol verwijderd");
                                    load();
                                  } catch (e) { toast.error(formatApiError(e)); }
                                }}
                                className="p-1.5 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded"
                                title="Verwijderen" data-testid={`role-delete-${r.key}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {(creating || editingRole) && registry && (
        <RoleEditor
          registry={registry}
          role={editingRole}
          onClose={() => { setEditingRole(null); setCreating(false); }}
          onSaved={async () => { setEditingRole(null); setCreating(false); await load(); }}
        />
      )}

      {duplicatingFrom && (
        <DuplicateDialog
          source={duplicatingFrom}
          onClose={() => setDuplicatingFrom(null)}
          onDone={async () => { setDuplicatingFrom(null); await load(); }}
        />
      )}
    </div>
  );
}

/* ─── Role editor dialog ─────────────────────────────── */
function RoleEditor({ role, registry, onClose, onSaved }) {
  const isEdit = !!role;
  const [name, setName] = useState(role?.name || "");
  const [key, setKey] = useState(role?.key || "");
  const [description, setDescription] = useState(role?.description || "");
  const [permissions, setPermissions] = useState(new Set(role?.permissions || []));
  const [disabled, setDisabled] = useState(role?.disabled || false);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!name.trim()) { toast.error("Naam is verplicht"); return; }
    if (!isEdit && !key.trim()) { toast.error("Key is verplicht"); return; }
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        description: description.trim(),
        permissions: Array.from(permissions),
      };
      if (isEdit) {
        payload.disabled = disabled;
        await adminApi.put(`/admin/rbac/roles/${role.id}`, payload);
      } else {
        await adminApi.post("/admin/rbac/roles", { ...payload, key: key.trim().toLowerCase().replace(/\s+/g, "_") });
      }
      toast.success(isEdit ? "Rol opgeslagen" : "Rol aangemaakt");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => !o && !saving && onClose()}>
      <DialogContent className="sm:max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="role-editor-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? `Rol wijzigen: ${role.name}` : "Nieuwe rol"}</DialogTitle>
          <DialogDescription>
            {isEdit && role.is_system && "Dit is een systeemrol. Je kunt permissies aanpassen, maar niet verwijderen."}
            {!isEdit && "Maak een nieuwe rol aan door naam en permissies te kiezen."}
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Naam *</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1"
                   data-testid="role-editor-name" />
          </div>
          <div>
            <Label className="text-xs">Key {!isEdit && "*"}</Label>
            <Input value={key} onChange={(e) => setKey(e.target.value)}
                   disabled={isEdit} className="mt-1 font-mono text-xs"
                   placeholder="warehouse_curacao" data-testid="role-editor-key" />
          </div>
          <div className="sm:col-span-2">
            <Label className="text-xs">Beschrijving</Label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)}
                      rows={2} className="mt-1 text-xs" data-testid="role-editor-description" />
          </div>
          {isEdit && !role.is_system && (
            <div className="sm:col-span-2 flex items-center gap-2">
              <input type="checkbox" checked={disabled}
                     onChange={(e) => setDisabled(e.target.checked)}
                     data-testid="role-editor-disabled" />
              <Label className="text-xs text-slate-700">Rol uitgeschakeld</Label>
            </div>
          )}
        </div>

        <div className="mt-3">
          <Label className="text-xs mb-2 block">
            Permissies ({permissions.size} geselecteerd)
          </Label>
          <PermissionMatrixEditor
            registry={registry}
            selectedPermissions={permissions}
            onChange={setPermissions}
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Annuleren</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-[#0055FF] text-white"
                  data-testid="role-editor-save">
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
            Opslaan
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ─── Duplicate dialog ────────────────────────────────── */
function DuplicateDialog({ source, onClose, onDone }) {
  const [newKey, setNewKey] = useState(`${source.key}_copy`);
  const [newName, setNewName] = useState(`${source.name} (kopie)`);
  const [saving, setSaving] = useState(false);

  const handleDuplicate = async () => {
    setSaving(true);
    try {
      await adminApi.post(`/admin/rbac/roles/${source.id}/duplicate`, {
        new_key: newKey.toLowerCase().replace(/\s+/g, "_"),
        new_name: newName,
      });
      toast.success("Rol gedupliceerd");
      onDone();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => !o && !saving && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="role-duplicate-dialog">
        <DialogHeader>
          <DialogTitle>Rol dupliceren</DialogTitle>
          <DialogDescription>Maak een kopie van "{source.name}" als startpunt.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <div>
            <Label className="text-xs">Nieuwe key *</Label>
            <Input value={newKey} onChange={(e) => setNewKey(e.target.value)}
                   className="mt-1 font-mono text-xs" data-testid="role-duplicate-key" />
          </div>
          <div>
            <Label className="text-xs">Nieuwe naam *</Label>
            <Input value={newName} onChange={(e) => setNewName(e.target.value)} className="mt-1"
                   data-testid="role-duplicate-name" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Annuleren</Button>
          <Button onClick={handleDuplicate} disabled={saving} className="bg-[#0055FF] text-white"
                  data-testid="role-duplicate-confirm">
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Copy className="w-4 h-4 mr-1" />}
            Dupliceren
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
